import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, X, Clock, IndianRupee, Wrench, FileText, Calendar, CircleCheck as CheckCircle, CircleAlert as AlertCircle, ChevronRight, ShieldCheck, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { format, differenceInDays, isPast, isToday } from 'date-fns';

interface NotificationItem {
  id: string;
  type: 'reminder' | 'maintenance_due' | 'tax_due' | 'meeting' | 'equipment' | 'overdue_fee' | 'visitor_approval' | 'community_tax';
  title: string;
  body: string;
  daysUntil: number | null;
  path: string;
  createdAt: Date;
  isRead: boolean;
}

const STORAGE_KEY = 'aw_read_notifications';

function getReadIds(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function markRead(ids: string[]) {
  try {
    const existing = getReadIds();
    ids.forEach(id => existing.add(id));
    const arr = Array.from(existing);
    if (arr.length > 500) arr.splice(0, arr.length - 500);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch {}
}

const TYPE_CONFIG: Record<NotificationItem['type'], { icon: React.ReactNode; color: string; bg: string }> = {
  reminder: { icon: <Bell className="w-4 h-4" />, color: 'text-[#16a085]', bg: 'bg-[rgba(22,160,133,0.12)]' },
  maintenance_due: { icon: <IndianRupee className="w-4 h-4" />, color: 'text-amber-600', bg: 'bg-amber-100' },
  overdue_fee: { icon: <AlertCircle className="w-4 h-4" />, color: 'text-red-600', bg: 'bg-red-100' },
  tax_due: { icon: <FileText className="w-4 h-4" />, color: 'text-orange-600', bg: 'bg-orange-100' },
  meeting: { icon: <Calendar className="w-4 h-4" />, color: 'text-teal-600', bg: 'bg-teal-100' },
  equipment: { icon: <Wrench className="w-4 h-4" />, color: 'text-slate-600', bg: 'bg-slate-100' },
  visitor_approval: { icon: <ShieldCheck className="w-4 h-4" />, color: 'text-amber-700', bg: 'bg-amber-100' },
  community_tax: { icon: <Users className="w-4 h-4" />, color: 'text-emerald-700', bg: 'bg-emerald-100' },
};

function urgencyLabel(days: number | null): { text: string; cls: string } {
  if (days === null) return { text: '', cls: '' };
  if (days < 0) return { text: `${Math.abs(days)}d overdue`, cls: 'text-red-600 font-semibold' };
  if (days === 0) return { text: 'Due today', cls: 'text-red-500 font-semibold' };
  if (days <= 3) return { text: `${days}d left`, cls: 'text-orange-500 font-semibold' };
  if (days <= 7) return { text: `${days}d left`, cls: 'text-amber-500' };
  return { text: `${days}d left`, cls: 'text-muted-foreground' };
}

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [readIds, setReadIds] = useState<Set<string>>(getReadIds);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const now = new Date();
      const items: NotificationItem[] = [];

      const [remindersRes, taxesRes, equipmentRes, meetingsRes, mfcRes, visitorApprovalsRes, communityTaxRes] = await Promise.all([
        supabase
          .from('recurring_reminders')
          .select('id, title, category, next_due_date, frequency')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .not('next_due_date', 'is', null)
          .lte('next_due_date', new Date(now.getTime() + 14 * 86400000).toISOString().split('T')[0])
          .order('next_due_date', { ascending: true })
          .limit(20),
        supabase
          .from('taxes')
          .select('id, tax_type, due_date, payment_status, property_id')
          .eq('user_id', user.id)
          .not('due_date', 'is', null)
          .not('payment_status', 'eq', 'paid')
          .lte('due_date', new Date(now.getTime() + 14 * 86400000).toISOString().split('T')[0])
          .order('due_date', { ascending: true })
          .limit(10),
        supabase
          .from('equipment')
          .select('id, name, type, next_maintenance_date, property_id')
          .not('next_maintenance_date', 'is', null)
          .lte('next_maintenance_date', new Date(now.getTime() + 14 * 86400000).toISOString().split('T')[0])
          .order('next_maintenance_date', { ascending: true })
          .limit(10),
        supabase
          .from('meetings')
          .select('id, meeting_type, meeting_date, property_id')
          .not('meeting_date', 'is', null)
          .gte('meeting_date', now.toISOString().split('T')[0])
          .lte('meeting_date', new Date(now.getTime() + 7 * 86400000).toISOString().split('T')[0])
          .order('meeting_date', { ascending: true })
          .limit(5),
        supabase
          .from('maintenance_fee_collections')
          .select(`
            id, period_month, period_year, amount_due, status,
            property_users!maintenance_fee_collections_property_user_id_fkey(user_id)
          `)
          .not('status', 'in', '("paid","waived")')
          .limit(20),
        supabase
          .from('visitor_approval_requests')
          .select('id, host_unit, host_user_id, created_at, visitor_log:visitor_logs(purpose)')
          .eq('status', 'pending')
          .eq('host_user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10),
        // Show community taxes due within 7 days OR already overdue — persist until paid
        supabase
          .from('community_tax_member_status')
          .select('id, assignment_id, status, community_tax_assignments!inner(tax_type, due_date, is_active)')
          .eq('user_id', user.id)
          .eq('status', 'pending')
          .eq('community_tax_assignments.is_active', true)
          .lte('community_tax_assignments.due_date', new Date(now.getTime() + 7 * 86400000).toISOString().split('T')[0])
          .limit(20),
      ]);

      remindersRes.data?.forEach((r: any) => {
        const days = differenceInDays(new Date(r.next_due_date), now);
        items.push({
          id: `reminder-${r.id}`,
          type: 'reminder',
          title: r.title,
          body: `${r.category} reminder`,
          daysUntil: days,
          path: '/reminders',
          createdAt: new Date(r.next_due_date),
          isRead: false,
        });
      });

      taxesRes.data?.forEach((t: any) => {
        const days = differenceInDays(new Date(t.due_date), now);
        items.push({
          id: `tax-${t.id}`,
          type: 'tax_due',
          title: t.tax_type,
          body: 'Tax payment due',
          daysUntil: days,
          path: '/taxes',
          createdAt: new Date(t.due_date),
          isRead: false,
        });
      });

      equipmentRes.data?.forEach((e: any) => {
        const days = differenceInDays(new Date(e.next_maintenance_date), now);
        items.push({
          id: `equip-${e.id}`,
          type: 'equipment',
          title: e.name,
          body: 'Scheduled maintenance due',
          daysUntil: days,
          path: '/equipment',
          createdAt: new Date(e.next_maintenance_date),
          isRead: false,
        });
      });

      meetingsRes.data?.forEach((m: any) => {
        const days = differenceInDays(new Date(m.meeting_date), now);
        items.push({
          id: `meeting-${m.id}`,
          type: 'meeting',
          title: m.meeting_type ? `${m.meeting_type.charAt(0).toUpperCase() + m.meeting_type.slice(1)} Meeting` : 'Meeting',
          body: 'Upcoming meeting',
          daysUntil: days,
          path: '/meetings',
          createdAt: new Date(m.meeting_date),
          isRead: false,
        });
      });

      mfcRes.data?.forEach((c: any) => {
        if (c.property_users?.user_id !== user.id) return;
        const dueDate = new Date(c.period_year, c.period_month - 1, 5);
        const days = differenceInDays(dueDate, now);
        const type: NotificationItem['type'] = days < 0 ? 'overdue_fee' : 'maintenance_due';
        const monthLabel = format(new Date(c.period_year, c.period_month - 1, 1), 'MMM yyyy');
        items.push({
          id: `mfc-${c.id}`,
          type,
          title: `Maintenance Fee — ${monthLabel}`,
          body: `₹${Number(c.amount_due).toLocaleString('en-IN')} ${days < 0 ? 'overdue' : 'due'}`,
          daysUntil: days,
          path: '/maintenance',
          createdAt: dueDate,
          isRead: false,
        });
      });

      visitorApprovalsRes.data?.forEach((req: any) => {
        const purpose = req.visitor_log?.purpose || 'visitor';
        items.push({
          id: `visitor-approval-${req.id}`,
          type: 'visitor_approval',
          title: `Visitor at Gate — Unit ${req.host_unit}`,
          body: `${purpose} · Awaiting your approval`,
          daysUntil: 0,
          path: '/visitors',
          createdAt: new Date(req.created_at),
          isRead: false,
        });
      });

      communityTaxRes.data?.forEach((row: any) => {
        const assignment = row.community_tax_assignments;
        if (!assignment) return;
        const days = differenceInDays(new Date(assignment.due_date), now);
        const taxLabel = (assignment.tax_type as string).replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
        items.push({
          id: `community-tax-${row.id}`,
          type: 'community_tax',
          title: `Community ${taxLabel}`,
          body: days < 0 ? 'Payment overdue' : days === 0 ? 'Due today' : `Due in ${days} day${days !== 1 ? 's' : ''}`,
          daysUntil: days,
          path: '/taxes',
          createdAt: new Date(assignment.due_date),
          isRead: false,
        });
      });

      const readSet = getReadIds();
      const sorted = items
        .map(n => ({ ...n, isRead: readSet.has(n.id) }))
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      setNotifications(sorted);
    } catch (err) {
      console.error('NotificationBell fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const unreadCount = notifications.filter(n => !readIds.has(n.id)).length;

  const handleOpen = () => {
    setOpen(v => !v);
  };

  const handleMarkAllRead = () => {
    const ids = notifications.map(n => n.id);
    markRead(ids);
    setReadIds(new Set(ids));
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const handleItemClick = (n: NotificationItem) => {
    markRead([n.id]);
    setReadIds(prev => new Set([...prev, n.id]));
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, isRead: true } : x));
    navigate(n.path);
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={handleOpen}
        className="relative flex items-center justify-center w-9 h-9 rounded-xl border border-border bg-card hover:bg-muted transition-colors shadow-sm"
        aria-label="Notifications"
      >
        <Bell className="w-4.5 h-4.5 text-foreground" style={{ width: '1.1rem', height: '1.1rem' }} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-80 max-w-[calc(100vw-5rem)] bg-card border border-border rounded-xl shadow-2xl z-[500] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-600 rounded-full font-medium">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded-lg hover:bg-muted transition-colors"
              >
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="py-10 flex flex-col items-center gap-2 text-muted-foreground">
                <div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
                <p className="text-xs">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-10 flex flex-col items-center gap-2 text-muted-foreground">
                <CheckCircle className="w-8 h-8 opacity-30" />
                <p className="text-sm font-medium">All clear!</p>
                <p className="text-xs">No upcoming reminders or dues</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.map(n => {
                  const cfg = TYPE_CONFIG[n.type];
                  const urgency = urgencyLabel(n.daysUntil);
                  const isRead = readIds.has(n.id);
                  return (
                    <button
                      key={n.id}
                      onClick={() => handleItemClick(n)}
                      className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/60 transition-colors ${isRead ? 'opacity-60' : ''}`}
                    >
                      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 ${cfg.bg} ${cfg.color}`}>
                        {cfg.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-1">
                          <p className={`text-xs font-semibold text-foreground truncate ${!isRead ? 'font-bold' : ''}`}>
                            {n.title}
                          </p>
                          {!isRead && (
                            <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                        {urgency.text && (
                          <p className={`text-xs mt-0.5 ${urgency.cls}`}>{urgency.text}</p>
                        )}
                      </div>
                      <ChevronRight className="flex-shrink-0 w-3.5 h-3.5 text-muted-foreground mt-1" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="px-4 py-2.5 border-t border-border bg-muted/30">
            <button
              onClick={() => { navigate('/reminders'); setOpen(false); }}
              className="w-full text-center text-xs text-primary font-medium hover:underline"
            >
              View all reminders
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
